import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import { addFilterDrawnLayer, updateProperties, setActiveDrawing } from '../../store/actions';
import './DrawShape.css';

export class DrawShapeComponent extends Component {
  constructor(props) {
    super(props);
    this.handleCreated = this.handleCreated.bind(this);
    this.onFeatureGroupReady = this.onFeatureGroupReady.bind(this);
    this.findMinMax = this.findMinMax.bind(this);
  }

  componentWillReceiveProps({ filters, activeShape, propertyType }) {
    if (propertyType !== 'areas' && !filters.has('shape') && this.layer) {
      this.layer.remove();
    }

    if (this.props.activeShape !== activeShape && this.editControl) {
      const control = this
        .editControl
        .leafletElement
        ._toolbars
        .draw
        ._modes[activeShape.toLowerCase()];
      if (control) {
        setTimeout(() => { control.button.click(); });
      }
    }
  }

  onFeatureGroupReady(drawingGroup) {
    const { drawnLayer, propertyType, backgroundColor } = this.props;
    const shapeOptions = {
      className: 'drawn-shape',
      clickable: true,
      color: backgroundColor,
      fill: true,
      fillColor: null,
      fillOpacity: 0.2,
      opacity: 0.5,
      showArea: true,
      stroke: true,
      weight: 4,
    };
    if (drawingGroup) {
      const leafletFG = drawingGroup.leafletElement;
      leafletFG.clearLayers();
    }
    if (drawnLayer && Object.keys(drawnLayer).length && drawingGroup && propertyType !== 'areas') {
      let shapeToAdd = {};
      const leafletFG = drawingGroup.leafletElement;
      const lat = drawnLayer.geometry.coordinates[1];
      const lng = drawnLayer.geometry.coordinates[0];
      if (drawnLayer.geometry.type === 'Circle') {
        shapeToAdd = new L.Circle([lat, lng], drawnLayer.geometry.radius, shapeOptions);
      } else {
        shapeToAdd = new L.GeoJSON(drawnLayer, shapeOptions);
      }
      leafletFG.addLayer(shapeToAdd);
    }
  }

  findMinMax(arr) {
    let minX = arr[0].lat;
    let maxX = arr[0].lat;
    let minY = arr[0].lng;
    let maxY = arr[0].lng;
    for (let i = 1; i < arr.length; i++) { // eslint-disable-line
      const x = arr[i].lat;
      const y = arr[i].lng;
      minX = (x < minX) ? x : minX;
      maxX = (x > maxX) ? x : maxX;
      minY = (y < minY) ? y : minY;
      maxY = (y > maxY) ? y : maxY;
    }
    return [minX, minY, maxX, maxY];
  }

  handleCreated(event) {
    if (this.layer) {
      this.layer.remove();
    }
    const { layer } = event;
    const geoJson = layer.toGeoJSON();

    if (event.layerType === 'circle') {
      geoJson.geometry.type = 'Circle';
      geoJson.geometry.radius = layer.getRadius();
    } else {
      // get width and height of polygon/rectangle
      const latlng = layer.getLatLngs()[0];
      const minMax = this.findMinMax(latlng);

      const startingPoint = new L.LatLng(minMax[0], minMax[1]);
      const highestPoint = new L.LatLng(minMax[2], minMax[1]);
      const widestPoint = new L.LatLng(minMax[0], minMax[3]);

      const width = startingPoint.distanceTo(widestPoint);
      const height = startingPoint.distanceTo(highestPoint);
      geoJson.geometry.width = width / 1609;
      geoJson.geometry.height = height / 1609;

      // get area - sq. mi. - used for polygon/rectangle
      let area = '';
      if (this.props.activeShape === 'Polygon') {
        area = L.GeometryUtil.geodesicArea(layer.getLatLngs()[0]);
        // area = area / 2589988.110336; // convert area from sq. meters to sq. miles
        area *= 0.00000038610215855; // convert area from sq. meters to sq. miles
      } else {
        area = (width / 1609) * (height / 1609);
      }
      geoJson.geometry.area = area;
    }

    this.layer = layer;
    const { filters, onShapeCreated } = this.props;
    onShapeCreated(new Map([...filters, ['shape', geoJson.geometry]]), geoJson);
  }

  render() {
    const { backgroundColor } = this.props;
    return (
      <FeatureGroup ref={drawingGroup => { this.onFeatureGroupReady(drawingGroup); }}>
        <EditControl
          position="topleft"
          onCreated={this.handleCreated}
          ref={ref => { this.editControl = ref; }}
          draw={{
            polyline: false,
            marker: false,
            circlemarker: false,
            circle: {
              metric: false,
              feet: false,
              shapeOptions: {
                color: backgroundColor,
              },
            },
            rectangle: {
              shapeOptions: {
                color: backgroundColor,
              },
            },
            polygon: {
              shapeOptions: {
                color: backgroundColor,
              },
            },
          }}
        />
      </FeatureGroup>
    );
  }
}

DrawShapeComponent.propTypes = {
  backgroundColor: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  onShapeCreated: PropTypes.func.isRequired,
  activeShape: PropTypes.string,
  propertyType: PropTypes.string,
  drawnLayer: PropTypes.object,
};

DrawShapeComponent.defaultProps = {
  filters: {},
};

/* istanbul ignore next */
export const mapStateToProps = (state, { propertyType }) => {
  const filters = (state[propertyType]) ? state[propertyType].filters : new Map();
  const sort = (state[propertyType]) ? state[propertyType].sort : [];
  const drawnLayer = (state[propertyType] && state[propertyType].filterDrawnLayer)
    ? state[propertyType].filterDrawnLayer
    : {};
  return {
    backgroundColor: state.viewer.backgroundColor,
    organizationId: state.organizationId,
    filters,
    sort,
    activeShape: state.draw,
    drawnLayer,
  };
};


/* istanbul ignore next */
const mapDispatchToProps = (dispatch, { propertyType }) => {
  return {
    onShapeCreated: (currentSort, organizationId) => (filters, layer) => {
      const sortString = filters.get('sort');
      const sort = sortString ? sortString.split(':') : currentSort;
      filters.delete('sort');
      dispatch(
        updateProperties(propertyType, filters, sort, organizationId),
      );
      dispatch(
        addFilterDrawnLayer(propertyType, layer),
      );
      dispatch(
        setActiveDrawing(''),
      );
    },
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { organizationId, sort } = stateProps;
  const { onShapeCreated } = dispatchProps;
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onShapeCreated: onShapeCreated(sort, organizationId),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(DrawShapeComponent);



// WEBPACK FOOTER //
// ./src/components/Map/DrawShape.js